import { FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { useTheme } from "@mui/material/styles";

import MDButton from "components/MDButton";
import File from "assets/Icons/file.svg?react";
import FileCircle from "assets/Icons/fileCircle.svg?react";
import { HMO } from "constants/hmo";
import { detectHmo } from "utils/hmoDetection";
import { useTermsModalStyles } from "./TermsModal.styles";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const isLeumit = detectHmo() === HMO.LEUMIT;
const TermsModalIcon = isLeumit ? FileCircle : File;

const TermsModal: FC<TermsModalProps> = ({ isOpen, onClose }) => {
  const { classes } = useTermsModalStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    closeButtonRef.current?.focus();

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = () => {
    onClose();
  };

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className={classes.backdrop}
      onClick={handleBackdropClick}
      data-test-id="terms-modal-backdrop"
    >
      <div
        className={classes.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
        onClick={handleModalClick}
        data-test-id="terms-modal"
      >
        <div className={classes.topBar}>
          <div className={classes.headerBlock}>
            <div className={classes.headerIcon} aria-hidden>
              <TermsModalIcon />
            </div>
            <div className={classes.headerBlockContent}>
              <div
                id="terms-modal-title"
                className={classes.headerTitle}
                data-test-id="terms-modal-title"
              >
                {t("common:TermsAndConditions:title")}
              </div>
              <div className={classes.headerDate} data-test-id="terms-modal-date">
                {t("common:TermsAndConditions:date")}
              </div>
            </div>
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            className={classes.closeButton}
            onClick={onClose}
            onKeyDown={handleCloseKeyDown}
            aria-label={t("common:TermsAndConditions:closeButton")}
            data-test-id="terms-modal-close-icon"
          >
            <CloseSharpIcon fontSize="small" />
          </button>
        </div>

        <div
          className={classes.content}
          data-test-id="terms-modal-content"
        >
          {t("common:TermsAndConditions:description")}
        </div>

        <div className={classes.footer}>
          <MDButton
            style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
            color="primary"
            variant="contained"
            type="button"
            onClick={onClose}
            className={classes.closeActionButton}
            data-test-id="terms-modal-close-button"
          >
            {t("common:TermsAndConditions:closeButton")}
          </MDButton>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
